import React, { useEffect, useRef, useCallback, useState } from 'react';

export interface UseIntersectionObserverOptions {
  root?: Element | null;
  rootMargin?: string;
  threshold?: number | number[];
  freezeOnceVisible?: boolean;
  initialIsIntersecting?: boolean;
}

export interface UseIntersectionObserverReturn {
  isIntersecting: boolean;
  entry: IntersectionObserverEntry | null;
  ref: React.RefCallback<Element>;
}

/**
 * Enhanced intersection observer hook with performance optimizations
 */
export function useIntersectionObserver(
  options: UseIntersectionObserverOptions = {}
): UseIntersectionObserverReturn {
  const {
    root = null,
    rootMargin = '0px',
    threshold = 0,
    freezeOnceVisible = false,
    initialIsIntersecting = false,
  } = options;

  const [entry, setEntry] = useState<IntersectionObserverEntry | null>(null);
  const [isIntersecting, setIsIntersecting] = useState(initialIsIntersecting);
  const [element, setElement] = useState<Element | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const frozenRef = useRef(false);

  // Callback ref to set the element
  const ref = useCallback((el: Element | null) => {
    setElement(el);
  }, []);

  // Create observer instance
  const createObserver = useCallback(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    if (!element) {
      return;
    }

    observerRef.current = new IntersectionObserver(
      ([entry]: IntersectionObserverEntry[]) => {
        if (!entry) return;

        const isIntersectingNow = entry.isIntersecting;

        setEntry(entry);

        if (freezeOnceVisible && isIntersectingNow) {
          frozenRef.current = true;
          setIsIntersecting(true);
          // Disconnect observer once visible to save resources
          observerRef.current?.disconnect();
        } else if (!frozenRef.current) {
          setIsIntersecting(isIntersectingNow);
        }
      },
      {
        root,
        rootMargin,
        threshold,
      }
    );

    observerRef.current.observe(element);
  }, [element, root, rootMargin, threshold, freezeOnceVisible]);

  // Setup observer when element or options change
  useEffect(() => {
    createObserver();

    return () => {
      observerRef.current?.disconnect();
    };
  }, [createObserver]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      observerRef.current?.disconnect();
    };
  }, []);

  return { isIntersecting, entry, ref };
}

/**
 * Multiple elements intersection observer hook
 */
export function useMultipleIntersectionObserver(
  options: UseIntersectionObserverOptions = {}
) {
  const { root = null, rootMargin = '0px', threshold = 0 } = options;

  const [entries, setEntries] = useState<
    Map<Element, IntersectionObserverEntry>
  >(new Map());
  const observerRef = useRef<IntersectionObserver | null>(null);
  const elementsRef = useRef<Set<Element>>(new Set());

  // Create observer instance
  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (observerEntries: IntersectionObserverEntry[]) => {
        setEntries((prev) => {
          const newEntries = new Map(prev);
          observerEntries.forEach((entry) => {
            newEntries.set(entry.target, entry);
          });
          return newEntries;
        });
      },
      {
        root,
        rootMargin,
        threshold,
      }
    );

    return () => {
      observerRef.current?.disconnect();
    };
  }, [root, rootMargin, threshold]);

  // Observe an element
  const observe = useCallback((element: Element) => {
    if (!observerRef.current || elementsRef.current.has(element)) {
      return;
    }

    elementsRef.current.add(element);
    observerRef.current.observe(element);
  }, []);

  // Unobserve an element
  const unobserve = useCallback((element: Element) => {
    if (!observerRef.current || !elementsRef.current.has(element)) {
      return;
    }

    elementsRef.current.delete(element);
    observerRef.current.unobserve(element);
    setEntries((prev) => {
      const newEntries = new Map(prev);
      newEntries.delete(element);
      return newEntries;
    });
  }, []);

  // Get intersection state for an element
  const getIntersectionState = useCallback(
    (element: Element) => {
      const entry = entries.get(element);
      return {
        isIntersecting: entry?.isIntersecting ?? false,
        entry: entry ?? null,
      };
    },
    [entries]
  );

  return {
    observe,
    unobserve,
    getIntersectionState,
    entries,
  };
}
